/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author ILya Babanov
 * @version 1.22.0
 * @since 1.19.0
 */

import styled, { keyframes } from 'styled-components';

import { ScrollbarStyled } from '@components/common';
import { colors } from '@theme/index';

interface ITableStyled {
    rowPadding: number;
    isInnerTable: boolean;
};

export const TableStyled = styled.div<ITableStyled>`

    .infiniti-scroll {
        overflow: unset !important;
    }

    .window-scroller-override {
        height: 100% !important;
        overflow: unset !important;
    }


    .rt-table {
      margin-bottom: ${props => props.isInnerTable ? 0 : '60px'};
    }

    .rt-thead {
        position: sticky;
        top: 0;
        z-index: 3;
        user-select: none;
        overflow: hidden;

        &.-header {
            box-shadow: none;
            background-color: ${colors.tableHeaderBgColor};
        }

        .rt-th {
            border: none;
            border-top: 1px solid ${colors.tableBorderTopColor};
            border-right: solid 1px transparent;
            border-bottom: solid 1px ${colors.tableGridBorderColor};
            white-space: normal;
            line-height: 1.6;
            align-self: auto;
            padding: 8px;
            background-color: #FFF;
        }

        &:hover {
            .rt-th {
                border-right: solid 1px ${colors.tableHeadGhostBorderColorHover};
            }

            .rt-th:last-child {
                border-right: solid 1px transparent;
            }
        }

        .header__cell {
            width: 100%;
        }
    }

    .rt-tbody {
        overflow: hidden;

        ${ScrollbarStyled};

        .rt-td {
            flex-wrap: wrap;
            background-color: #FFF;
            padding: ${props => props.rowPadding}px 8px;
            border-bottom: solid 1px ${colors.tableGridBorderColor};
        }

        .rt-tr-group {
            border-bottom: none;
        }

        .rt-tr-group .rt-tr-checked .rt-td {
            background-color: ${colors.tableCellBgColorChecked};
        }

        .rt-tr-group .rt-tr-delete {
            align-items: center;
            justify-content: center;
        }

        .rt-tr-group:hover .rt-td {
            background: ${colors.tableCellBgColorHover};
        }
    }

    .sticky {
        .header,
        .footer {
            position: sticky;
            z-index: 1;
            width: fit-content;
        }

        .header {
            top: 0;
            box-shadow: 0 3px 3px #ccc;
        }

        .footer {
            bottom: 0;
            box-shadow: 0px -3px 3px #ccc;
        }

        .body {
            position: relative;
            z-index: 0;
        }

        [data-sticky-td] {
            position: sticky;
        }

        [data-sticky-last-left-td] {
            border-right: solid 1px ${colors.tableGridBorderColor};
        }

        &.scrolled [data-sticky-last-left-td] {
            border-right-color: transparent;
            box-shadow: 1px 0 0 0 rgba(150, 150, 150, 0.25),
            2px 0 0 0 rgba(150, 150, 150, 0.20),
            3px 0 0 0 rgba(150, 150, 150, 0.15),
            4px 0 0 0 rgba(150, 150, 150, 0.10),
            5px 0 0 0 rgba(150, 150, 150, 0.05);
        }

        .table__tooltip-text {
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
        }
    }

    .resizer {
        display: inline-block;
        width: 5px;
        height: 100%;
        position: absolute;
        right: 0;
        top: 0;
        transform: translateX(50%);
        z-index: 1;
        cursor: col-resize !important;

        &:before {
            content: ' ';
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            display: none;
            width: 6px;
            height: 100%;
            background: ${colors.tableResizerColorHover};
        }

        &:hover, &:active {
            &:before {
                display: block;
            }
        }
    }
`;

const tableLoaderShimmer = keyframes`
    0% {
        background-position: -50px;
    }
    100% {
        background-position: 1150px;
    }
`;

export const TableLoaderStyled = styled.div`
    position: sticky;
    top: 83px;
    height: 2px;
    width: 100%;
    overflow: hidden;
    backface-visibility: hidden;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
    animation-fill-mode: forwards;
    animation-duration: 1.2s;
    animation-name: ${tableLoaderShimmer};
    background: ${colors.tableLoaderBackgroundGradient};
    z-index: 4;
`;
